import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { OrderItem } from '../../../common/model/dto/order/order-item';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import StringUtils from '../../../common/utils/services/string-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvOrderItemsTableProps {
  items: OrderItem[];
  isEditable: boolean;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}

const OvOrderItemsTable: FC<OvOrderItemsTableProps> = ({
  items,
  isEditable,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <Empty>{t('orders.details.item.noItems')}</Empty>;
  }

  return (
    <ScrollRegion>
      <Table>
        <thead>
          <tr>
            <th scope="col">{t('orders.details.item.productAndSku')}</th>
            <NumericHeader scope="col">
              {t('orders.details.item.price')}
            </NumericHeader>
            <NumericHeader scope="col">
              {t('orders.details.item.discount')}
            </NumericHeader>
            <NumericHeader scope="col">
              {t('orders.details.item.quantityShort')}
            </NumericHeader>
            {isEditable && <th scope="col">{t('common.actions.title')}</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={`${item.sku}-${index}`}>
              <td>
                <strong>{StringUtils.displayValue(item.name)}</strong>
                <Sku>{StringUtils.displayValue(item.sku)}</Sku>
              </td>
              <NumericCell>
                {item.price === undefined || item.price === null
                  ? '-'
                  : OrderDataUtils.formatAmount(item.price)}
              </NumericCell>
              <NumericCell>
                {item.discount
                  ? `−${OrderDataUtils.formatAmount(item.discount)}`
                  : '—'}
              </NumericCell>
              <NumericCell>{item.quantity ?? 1}</NumericCell>
              {isEditable && (
                <td>
                  <RowActions>
                    <button
                      type="button"
                      aria-label={t('orders.details.item.editNamed', {
                        name: item.name ?? item.sku,
                      })}
                      onClick={() => onEdit(index)}
                    >
                      {t('common.actions.edit')}
                    </button>
                    <button
                      type="button"
                      aria-label={t('orders.details.item.removeNamed', {
                        name: item.name ?? item.sku,
                      })}
                      onClick={() => onRemove(index)}
                    >
                      {t('common.actions.delete')}
                    </button>
                  </RowActions>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollRegion>
  );
};

export default OvOrderItemsTable;

const ScrollRegion = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${Variables.fontSizes.MEDIUM};

  th {
    padding: 0.25rem 0.875rem 0.625rem 0;
    border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
    font-weight: normal;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 0.625rem 0.875rem 0.625rem 0;
    border-bottom: 1px solid rgb(235, 237, 240);
    vertical-align: top;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const NumericHeader = styled.th`
  && {
    text-align: right;
  }
`;

const NumericCell = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Sku = styled.span`
  display: block;
  margin-top: 0.1875rem;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;

  button {
    min-height: 1.75rem;
    padding: 0.25rem 0.375rem;
    border: 0;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    background: transparent;
    color: ${Colours.OV_BASE};
    font: inherit;
    font-size: ${Variables.fontSizes.SMALL};
    cursor: pointer;
  }

  button:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  button:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const Empty = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;
